import { KeyboardEvent, MouseEvent, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ContactForm } from './ContactForm';
import { site } from './content';
import { resolveRoute } from './routes';

const navigation = [
  ['/services', 'Услуги'],
  ['/prices', 'Цены'],
  ['/projects', 'Клиенты и проекты'],
  ['/company', 'О компании'],
  ['/contacts', 'Контакты'],
] as const;

function NavLinks({ path, onNavigate, linkClassName }: { path: string; onNavigate?: () => void; linkClassName?: string }) {
  return <>{navigation.map(([href, label]) => <a className={linkClassName} key={href} href={href} aria-current={path === href ? 'page' : undefined} onClick={onNavigate}>{label}</a>)}</>;
}

export function BrandLockup({ inverse = false }: { inverse?: boolean }) {
  const cursor = useRef<SVGPathElement>(null);
  const replay = () => {
    const element = cursor.current;
    if (!element) return;
    element.classList.remove('brand-cursor-run');
    void element.getBoundingClientRect();
    element.classList.add('brand-cursor-run');
  };

  useEffect(() => { replay(); }, []);

  return <a className={`brand${inverse ? ' inverse' : ''}`} href="/" aria-label="СпаркСофт — на главную" onPointerEnter={replay} onFocus={replay}>
    <span className="brand-mark" aria-hidden="true"><svg viewBox="0 0 40 40" focusable="false"><rect x="1" y="1" width="38" height="38" rx="9" /><path className="brand-chevron" d="M10 9 21 20 10 31" /><path ref={cursor} className="brand-cursor" d="M21 29h10" /></svg></span>
    <span className="brand-wordmark">СпаркСофт</span>
  </a>;
}

export function SiteHeader({ path }: { path: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    return () => { document.body.style.overflow = previousOverflow; };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    requestAnimationFrame(() => trigger.current?.focus());
  };

  const onDrawerKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }
    if (event.key !== 'Tab' || !drawer.current) return;
    const focusable = [...drawer.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  return (
    <header className="site-header">
      <div className="container header-row">
        <BrandLockup />
        <nav className="desktop-nav" aria-label="Основная навигация">
          <NavLinks path={path} />
          <a className="button primary compact header-cta" href="/contacts#request"><span className="header-cta-shade" aria-hidden="true" /><span className="header-cta-label">Обсудить задачу</span></a>
        </nav>
        <button ref={trigger} type="button" className="menu-button" aria-expanded={menuOpen} aria-controls="mobile-menu" onClick={() => setMenuOpen(true)}>Меню</button>
      </div>
      {menuOpen && (
        <div className="menu-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeMenu(); }}>
          <div ref={drawer} id="mobile-menu" className="menu-drawer" role="dialog" aria-modal="true" aria-labelledby="menu-title" onKeyDown={onDrawerKeyDown}>
            <div className="menu-heading"><strong id="menu-title">Навигация</strong><button ref={closeButton} type="button" onClick={closeMenu}>Закрыть</button></div>
            <nav aria-label="Мобильная навигация"><NavLinks path={path} onNavigate={() => setMenuOpen(false)} /><a className="button primary" href="/contacts#request">Обсудить задачу</a></nav>
          </div>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  const organization = site.organization;
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <BrandLockup inverse />
          <p>Аналитика, проектирование, разработка, миграция и сопровождение цифровых решений.</p>
          <a className="footer-link" href={`mailto:${organization.email}`}>{organization.email}</a>
          <p className="footer-accreditation"><a href="https://www.gosuslugi.ru/itorgs" target="_blank" rel="noopener noreferrer">Компания входит в реестр аккредитованных ИТ-компаний</a></p>
        </div>
        <nav aria-label="Разделы сайта"><strong>Разделы</strong><NavLinks path="" linkClassName="footer-link" /></nav>
        <nav aria-label="Правовая информация"><strong>Документы</strong><a className="footer-link" href="/legal/privacy">Политика ПД</a><a className="footer-link" href="/legal/consent">Согласие на ПД</a><a className="footer-link" href="/legal/cookies">Политика cookies</a></nav>
        <div className="footer-facts"><strong>Реквизиты</strong><span>{organization.legalFormName}</span><span>ИНН {organization.inn}</span><span>ОГРН {organization.ogrn}</span><span>{organization.address}</span></div>
      </div>
      <div className="container footer-bottom"><span>© 2026 ООО «СпаркСофт»</span></div>
    </footer>
  );
}

export function Breadcrumbs({ path }: { path: string }) {
  if (path === '/') return null;
  const route = resolveRoute(path);
  return <nav className="breadcrumbs" aria-label="Хлебные крошки"><ol><li><a href="/">Главная</a></li>{path.startsWith('/legal/') && <li><span aria-hidden="true">/</span><span>Правовая информация</span></li>}<li aria-current="page"><span aria-hidden="true">/</span><span>{route.h1}</span></li></ol></nav>;
}

function ContactDialog({ open, onDismiss }: { open: boolean; onDismiss: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!open || !element || typeof element.showModal !== 'function') return;
    if (!element.open) element.showModal();
    const frame = requestAnimationFrame(() => nameInput.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      if (element.open) element.close();
    };
  }, [open]);

  const trapFocus = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Tab' || !dialog.current) return;
    const focusable = [...dialog.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const dismiss = () => onDismiss();

  return <dialog ref={dialog} className="contact-dialog" aria-labelledby="contact-dialog-title" aria-describedby="contact-dialog-description" onCancel={(event) => { event.preventDefault(); dismiss(); }} onMouseDown={(event) => { if (event.target === event.currentTarget) dismiss(); }} onKeyDown={trapFocus}>
    <div className="contact-dialog-heading"><div><p className="eyebrow">Форма обращения</p><h2 id="contact-dialog-title">Расскажите о задаче</h2><p id="contact-dialog-description">Оставьте один контакт и кратко опишите задачу. Ответим в течение рабочего дня.</p></div><button className="dialog-close" type="button" aria-label="Закрыть форму" onClick={dismiss}>×</button></div>
    <ContactForm idPrefix="modal-field" initialFocusRef={nameInput} showHeading={false} warmChallengeOnMount={open} />
  </dialog>;
}

export function PageLayout({ path, children }: PropsWithChildren<{ path: string }>) {
  const route = resolveRoute(path);
  const [contactOpen, setContactOpen] = useState(false);
  const contactTrigger = useRef<HTMLAnchorElement>(null);

  const closeContact = () => {
    setContactOpen(false);
    requestAnimationFrame(() => {
      if (contactTrigger.current?.isConnected) contactTrigger.current.focus();
      else document.querySelector<HTMLButtonElement>('.menu-button')?.focus();
    });
  };

  const openContact = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
    const origin = event.target as Element;
    const trigger = origin.closest<HTMLAnchorElement>('a[href="/contacts#request"]');
    if (!trigger) return;
    event.preventDefault();
    contactTrigger.current = trigger;
    setContactOpen(true);
  };

  return (
    <div className="site-shell" onClick={openContact}>
      <a className="skip-link" href="#main">Перейти к содержанию</a>
      <SiteHeader path={path} />
      <main id="main" data-content-marker={route.marker}>{children}</main>
      <SiteFooter />
      <ContactDialog open={contactOpen} onDismiss={closeContact} />
    </div>
  );
}

export function PageIntro({ eyebrow, title, lead, dark = false, compact = false }: { eyebrow?: string; title: string; lead: string; dark?: boolean; compact?: boolean }) {
  return <section className={`page-intro${dark ? ' dark' : ''}${compact ? ' compact' : ''}`}><div className="container"><div className="narrow">{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1><p className="lead">{lead}</p></div></div></section>;
}

export function PricingNotice() {
  return <div className="fact-notice"><strong>Существенные условия</strong><p>Цены указаны без учета НДС. При наличии обязанности начислить НДС он добавляется по действующей ставке. Итоговая цена и состав результата фиксируются договором после обследования. Опубликованные суммы — минимальные пороги коммерческого предложения, не публичная оферта и не цены ранее выполненных кейсов.</p></div>;
}

export function CTASection() {
  return <section className="cta-section"><div className="container"><div><h2>Расскажите о задаче</h2><p>Первый ответ — в течение одного рабочего дня. Итоговый состав и цена фиксируются договором после обследования.</p></div><a className="button primary" href="/contacts#request">Обсудить задачу</a></div></section>;
}

export const formatDate = (value: string) => new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
